import { useEffect, useState } from 'react';

const pressedKeys = new Set<string>();
const listeners: Array<() => void> = [];

/**
 * Sets up global keydown and keyup event listeners to track currently pressed keys.
 */
function setupKeyListeners() {
    if (typeof window === 'undefined') {
        return;
    }

    function downHandler(event: KeyboardEvent) {
        pressedKeys.add(event.key.toLowerCase());
        listeners.forEach((listener) => listener());
    }

    const upHandler = (event: KeyboardEvent) => {
        pressedKeys.delete(event.key.toLowerCase());
        listeners.forEach((listener) => listener());
    };

    window.addEventListener('keydown', downHandler, { capture: true });
    window.addEventListener('keyup', upHandler, { capture: true });
}

/**
 * Adds a listener that is called whenever the set of pressed keys changes.
 */
function addPressedKeyListener(listener: () => void) {
    listeners.push(listener);
    return () => {
        const index = listeners.indexOf(listener);
        if (index !== -1) {
            listeners.splice(index, 1);
        }
    };
}

setupKeyListeners();

/**
 * Function to check if a specific key is currently pressed.
 */
export function checkIsKeyPressed(key: string): boolean {
    return pressedKeys.has(key.toLowerCase());
}

/**
 * Hook that tracks if a specific key is currently pressed.
 */
export function useIsKeyPressed(key: string): boolean {
    const [pressed, setPressed] = useState(() => checkIsKeyPressed(key));
    useEffect(() => addPressedKeyListener(() => setPressed(checkIsKeyPressed(key))), [key]);
    return pressed;
}
